/*
 * @Author       : Zhelin Cheng
 * @Date         : 2021-11-05 15:37:54
 * @LastEditors  : 程哲林
 * @LastEditTime : 2024-04-17 17:24:56
 * @FilePath     : /marketing-introduce-ssr/utils/index.ts
 * @Description  : 未添加文件描述
 */

// import wx from 'jweixin-sdk-official'
import * as Sentry from '@sentry/nextjs';
import storage from '@woulsl/storage/session';
import JsBridge from '@woulsl-app/js-bridge';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import Cookies from 'js-cookie';
import QS from 'querystring';

import { envName, isBrowser, MINI_INFO, POSTER_ZOOM } from '@/common/const';
import { AUTH_REDIRECT_PARAMS, AUTH_SIGN_URL } from '@/common/const';
import showWxQrCode from '@/components/wxQrCode';

import { StructureType } from './poster';
import { sensClickInitiative, sensElementView } from './sensors';

dayjs.extend(duration);

export function isImage(file: File) {
  const fileName = file.name || '';

  const fileExtension = fileName.split('.')?.pop()?.toLowerCase() || '';

  // 允许的图片文件扩展名
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'heic'];

  // 检查文件扩展名是否在允许的范围内
  if (allowedExtensions.includes(fileExtension)) {
    return true;
  } else {
    return false;
  }
}

// 判断是否为小窗
export const isMiniWindow = isBrowser && navigator.userAgent.includes('WindowType/window');

/**
 * 生成随机串
 * @param len 长度
 * @returns
 */
export function randomString(len = 8) {
  const $chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'; /** **默认去掉了容易混淆的字符oOLl,9gq,Vv,Uu,I1*** */
  const maxPos = $chars.length;
  let pwd = '';
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
}

export const qsParse = <T = any>(): T => {
  if (isBrowser) {
    const query = QS.parse(window.location.search.replace(/^\?/, '')) || {};
    const newQuery: any = {};
    for (const [key, val] of Object.entries(query)) {
      if (Array.isArray(val)) {
        const [val0] = val;
        newQuery[key] = val0;
      } else {
        newQuery[key] = val;
      }
    }
    return newQuery;
  } else {
    return {} as unknown as any;
  }
};

/**
 * 错误上报
 * @param err 错误
 */
export function reportError(error: any = {}): void {
  try {
    const exception = error?.message || error?.originalError || error?.error || error;
    if (isBrowser) {
      Sentry.captureException(exception, {
        level: Sentry.Severity.Error
      });
    }
  } catch (e) {
    console.error(e, error);
  }
}

/**
 * 检测浏览器环境是否是jojo app
 * eg：
 */
export const isJoJoBrowser = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /JoJoVersion|JoJoWebViewSymbol/i;
  return reg.test(ua);
};

export const isJoJoAllBrowser = () => {
  if (!isBrowser) {
    return false;
  }
  const ua = window.navigator.userAgent;
  const reg = /JoJoVersion|JoJoWebViewSymbol/i;
  return reg.test(ua);
};

export const isJoJoReadBrowser = () => {
  if (!isBrowser) {
    return false;
  }
  // 判断是否是jojo app
  const ua = window.navigator.userAgent;
  const reg = /JoJoAppFrom\/read/i;
  return isJoJoBrowser() && reg.test(ua);
};

/**
 * 检测浏览器环境是否是微信
 * eg：
 */
export const isWXBrowser = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /MicroMessenger/i;
  return reg.test(ua);
};

/**
 * 只是微信app内嵌浏览器，非小程序或其他浏览器
 * @returns {boolean|boolean}
 */
export const isWXAppBrowser = (): boolean => {
  if (!isBrowser) {
    return false;
  }
  const ua = window.navigator.userAgent;
  const reg = /miniprogram/i;

  return !reg.test(ua) && isWXBrowser();
};

/**
 * 微信小程序内嵌浏览器
 * @returns {boolean|boolean}
 */
export const isWXMiniAppBrowser = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /miniprogram/i;

  return reg.test(ua);
};

export const ownMiniApp = () => {
  const ua = window.navigator.userAgent;
  const appId = ['wx1fbbaa6654693d53', 'wx5a031e01d61da08a'];

  const aid = appId.find((id: string) => {
    return ua.indexOf(id) > -1;
  });

  return (isWXMiniAppBrowser() && aid) || '';
};

/**
 * 检测浏览器环境是否是抖音
 * eg：
 */
export const isTikTok = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /aweme/i;
  return reg.test(ua);
};

/**
 * 检测浏览器环境是否是快手
 * eg：
 */
export const isKuaiShou = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /kwai/i;
  return reg.test(ua);
};

/**
 * 检测浏览器环境是否是百度app
 * eg：
 */
export const isBaiduBoxApp = (): boolean => {
  if (!isBrowser) {
    return false;
  }

  const ua = window.navigator.userAgent;
  const reg = /baiduboxapp/i;
  return reg.test(ua);
};

/**
 * 检测浏览器环境是否为小程序webview
 * eg：
 */
export const getIsMiniProgramEnv = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const u = window.navigator.userAgent;
    if (/miniProgram/gim.test(u)) {
      resolve(true);
    } else if (window?.__wxjs_environment === 'miniprogram') {
      resolve(true);
    } else {
      if (isWXBrowser() && window?.wx) {
        // ios的ua中无miniProgram，但都有MicroMessenger（表示是微信浏览器）
        window.wx.miniProgram.getEnv((res: any) => {
          resolve(Boolean(res.miniprogram));
        });
      } else {
        resolve(false);
      }
    }
  });
};

/**
 * 获取平台信息0App 1微信 2小程序 3其他
 * @returns number
 */
export const getCurrPlatform = async (report = false): Promise<number> => {
  try {
    const isMiniApp = await getIsMiniProgramEnv();

    if (isMiniApp) {
      return 2;
    }

    if (isJoJoBrowser()) {
      return report ? 3 : 0;
    }

    if (isWXBrowser()) {
      return 1;
    }
    return report ? 10 : 3;
  } catch (e) {
    return report ? 10 : 3;
  }
};

export const getCurrPlatformText = async (): Promise<string> => {
  const platform = await getCurrPlatform();
  return ['APP内', '微信内', '微信小程序', '其他'][platform];
};

// 补全0
export function completeDate(value: number) {
  return value < 10 ? `0${value}` : value;
}

export function getNowFormatDay(nowDate = new Date(), char = '-') {
  const day = nowDate.getDate();
  const month = nowDate.getMonth() + 1;
  const year = nowDate.getFullYear();
  // 补全0，并拼接
  return year + char + completeDate(month) + char + completeDate(day);
}

export function compareVersion(version1: string, version2: string) {
  const arr1 = version1.split('.');
  const arr2 = version2.split('.');
  const length1 = arr1.length;
  const length2 = arr2.length;
  const minlength = Math.min(length1, length2);
  let i = 0;
  for (i; i < minlength; i++) {
    const a = parseInt(arr1[i], 10);
    const b = parseInt(arr2[i], 10);
    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    }
  }
  if (length1 > length2) {
    for (let j = i; j < length1; j++) {
      if (parseInt(arr1[j], 10) !== 0) {
        return 1;
      }
    }
    return 0;
  } else if (length1 < length2) {
    for (let j = i; j < length2; j++) {
      if (parseInt(arr2[j], 10) !== 0) {
        return -1;
      }
    }
    return 0;
  }
  return 0;
}

export function shareWx(
  data: {
    title: string;
    desc: string;
    url: string;
    scene: number;
    shareType: number;
    image: string;
  },
  cb?: (res: any) => void
) {
  const u = navigator.userAgent;
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1; // android终端

  const mc = u.match(/JoJoWebViewSymbol\/\d+\.\d+\.?\d+?/gm);
  const appObj: Record<string, string> = {};
  mc?.forEach((str: string) => {
    const item = str.split('/');
    const [key, val] = item;
    appObj[key] = val;
  });

  let dataScene = data.scene;
  // 兼容叫叫阅读低级版本
  if (
    u.indexOf('/read') >= 0 &&
    isAndroid &&
    compareVersion(appObj.JoJoWebViewSymbol, '1.30.0') <= 0
  ) {
    dataScene = data.scene ? 0 : 1;
    // data.scene = data.scene ? 0 : 1;
  }

  const bridge: any = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;

  if (canUseBridge) {
    bridge.call(
      'shareToWX',
      {
        ...data,
        scene: dataScene
      },
      (res: any = {}) => {
        if (typeof cb === 'function') {
          cb(res);
        }
      }
    );
  } else {
    reportError('不支持canUseBridge');
  }
}

export function openMiniApp(url: string, options?: any) {
  const bridge: any = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
  // 默认为1
  let nd = 1;
  let { userName, miniProgramType } = MINI_INFO;
  if (options) {
    nd = options?.needLogin ?? nd;
    userName = options?.userName ?? userName;
    miniProgramType = Number(options?.miniProgramType ?? miniProgramType);
  }
  if (canUseBridge) {
    bridge
      .call('launchMiniProgram', {
        path: `/pages/transform_space/main?type=webview&needLogin=${nd}&linkUrl=${encodeURIComponent(
          url
        )}`,
        userName,
        miniProgramType
      })
      .then((res: any) => {
        console.log('bridge===', res);
      });
  } else {
    reportError('不支持canUseBridge');
  }
}

/* function isiOSVersionBelow16(): boolean {
  // 获取用户当前 iOS 版本
  const { userAgent } = navigator;
  const iOSVersionMatch = userAgent.match(/(iPhone|iPad) OS (\d+)/);
  if (iOSVersionMatch) {
    const device = iOSVersionMatch[1];
    const iOSVersion = parseInt(iOSVersionMatch[2], 10);
    // 判断版本是否低于 16
    if ((device === 'iPhone' || device === 'iPad') && iOSVersion < 16) {
      return true;
    }
  }
  return false;
} */

// 有分享海报，复制内容丢失
// 生成的海报超过200kb，复制的时候复制内容就会丢失
// ios16+，APP => 拦截
// APP发起分享过程中，塞复制内容，可以，500ms左右
// 但会导致拉不起实际的分享功能（概率），1/5概率会出现

/**
 * 是否ios设备
 * @returns
 */
export const isIOS = () => {
  if (!isBrowser) {
    return false;
  }
  return /iPhone|iPad|iPod|Macintosh/gim.test(window.navigator.userAgent);
};

/**
 * 是否ios平板设备
 * @returns
 */
export const isIosIpad = () => {
  if (!isBrowser) {
    return false;
  }
  return /iPad/gim.test(navigator.userAgent);
};

/**
 * APP iOS6+复制功能的兼容
 * 默认情况下，生成的海报超过200kb，复制的时候复制内容就会丢失
 * @param str
 * @returns
 */
export const ios16CopyString = (str: string, timeout = 700) => {
  return new Promise((resolve) => {
    if (
      /iPhone|iPad/gim.test(navigator.userAgent) &&
      // !isiOSVersionBelow16() &&
      typeof document.execCommand === 'function' &&
      str
    ) {
      const copyDom = document.createElement('textarea');
      copyDom.value = str;
      document.body.appendChild(copyDom);
      setTimeout(() => {
        try {
          copyDom.select();
          document.execCommand('copy');
          document.body.removeChild(copyDom);
          resolve(true);
        } catch (err) {
          resolve(false);
        }
      }, timeout);
    } else {
      resolve(false);
    }
  });
};

export function scrollToElement(elementId: string, yOffset = 0) {
  try {
    const element = document.getElementById(elementId);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo(0, y);
      /* if ('scrollBehavior' in document.documentElement.style) {
        // 支持 scrollBehavior 属性，可以平滑滚动
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      } else {
        // 不支持 scrollBehavior 属性，使用传统的滚动方法
        window.scrollTo(0, y);
      } */
    }
  } catch (e) {
    console.error(e);
  }
}

export const tinmanWxtrigger = async (
  eventName: 'shareImageToWXCircle' | 'shareImageToWX',
  data: any,
  scene?: number,
  cb?: (res: any) => void,
  extra?: {
    activityId?: string;
    activityLabel?: string;
    webPageId?: string;
    channelNo?: string;
  }
) => {
  if (!isBrowser) {
    return;
  }

  let sceneNum = scene;

  if (typeof scene !== 'number') {
    sceneNum = eventName === 'shareImageToWXCircle' ? 1 : 0;
  }

  const isObj = typeof data !== 'string';
  const bridge: any = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;

  if (!canUseBridge) {
    const alt = window.alert;
    alt('当前环境不支持分享，请截图保存后分享');
    return;
  }
  let isJJWeChatInstalled = true;
  const installedRes: {
    status: number;
    data: { isInstalled: boolean };
  } = await bridge.isWechatInstalled();

  if (installedRes.status === 200) {
    isJJWeChatInstalled = Boolean(installedRes.data.isInstalled);
  }
  if (!isJJWeChatInstalled) {
    const { activityId, activityLabel, channelNo, webPageId } = extra || {};
    showWxQrCode({
      onShow: () => {
        sensElementView({
          c_element_name: '活动_未安装微信弹窗曝光',
          activity_id: activityId,
          activity_label: activityLabel,
          channel_no: channelNo || '',
          material_ids: [webPageId?.toString()]
        });
      },
      onHide: () => {
        sensClickInitiative({
          $element_name: '活动_未安装微信弹窗_关闭按钮点击',
          activity_id: activityId,
          activity_label: activityLabel,
          channel_no: channelNo || '',
          material_ids: [webPageId?.toString()]
        });
      }
    });
    return;
  }
  if (!isObj) {
    bridge.call(
      'shareToWX',
      {
        title: '',
        desc: '',
        url: '',
        scene: sceneNum,
        shareType: 1,
        image: data
      },
      (res: any = {}) => {
        if (typeof cb === 'function') {
          cb(res);
        }
      }
    );
  } else {
    const bridgeData = {
      appId: envName === 'pro' ? 'gh_ef0f0e30ef89' : 'gh_87f2400c676b',
      path: `/pages/transform_space/main?type=webview&needLogin=1&linkUrl=${encodeURIComponent(
        data.url
      )}`,
      type: envName === 'pro' ? 0 : 2,
      ...data
    };
    // 只针对海报页面进行改动
    if (data?.useMiniAppId) {
      bridgeData.appId = envName === 'pro' ? 'gh_8a438944f89f' : 'gh_f9eeb4f1101d';
      if (data?.useMiniAppId !== 'h5') {
        const query = qsParse();
        bridgeData.path = `/pages/transform_landing/main?${QS.stringify({
          inviteCode: data.memoInviteCode,
          channel: query?.channel,
          channelNo: query?.channelNo
        })}`;
      }
    }

    if (bridgeData.useMiniAppId) {
      delete bridgeData.useMiniAppId;
    }
    if (bridgeData.memoInviteCode) {
      delete bridgeData.memoInviteCode;
    }

    bridge.call('shareMiniProgram', bridgeData, (res: any = {}) => {
      if (typeof cb === 'function') {
        cb(res);
      }
    });
  }
};

/**
 * 小程序内webview跳转到小程序
 * eg：
 */
export const miniProgramNavigateTo = ({ url, params = {} }: any) => {
  const path = Object.keys(params || {}).length > 0 ? `${url}?${QS.stringify(params)}` : url;
  window.wx.miniProgram.navigateTo({ url: path });
};

/**
 * 跳转到小程序首页TAB
 * @param param0
 */
export const miniProgramSwitchTab = ({ url, params = {} }: any) => {
  const path = Object.keys(params).length > 0 ? `${url}?${QS.stringify(params)}` : url;
  window.wx.miniProgram.switchTab({ url: path });
};

/**
 * 判断是否是jojo_read小程序
 */
export const isMiniAppNotJoJo = () => {
  const ua = window.navigator.userAgent.toLocaleLowerCase();
  const inWxMini = isWXMiniAppBrowser();
  const miniAppId = Cookies.get('miniAppId') || (ua?.split?.('miniprogram/')?.[1] ?? '');
  const jojoAppId = ['wx1fbbaa6654693d53', 'wx5a031e01d61da08a'];
  return inWxMini && !jojoAppId.includes(miniAppId);
};
/**
 * 计算商品价格
 * @param val
 * @returns
 */
export function computePrice(val: string | number) {
  const price = typeof val === 'number' ? val : Number(val);
  return parseFloat((price / 100).toFixed(3));
}

export const getEnvEnum = async () => {
  if (isJoJoBrowser()) {
    return 3;
  }
  const isInMiniprogram = await getIsMiniProgramEnv();
  if (isInMiniprogram) {
    return 2;
  }
  if (isWXBrowser()) {
    return 1;
  }

  return 1;
};

export function durationTime(newTime: number): string;
export function durationTime(newTime: number, type: 'obj'): duration.Duration;
export function durationTime(newTime: number, type: 'format'): { format: (str: string) => string };
export function durationTime(
  newTime: number,
  type?: string
): duration.Duration | string | { format: (str: string) => string } {
  const dd = dayjs.duration(newTime);
  if (type === 'obj') {
    return dd;
  } else if (type === 'format') {
    return {
      format: (str: string) => {
        return dd.format(str.replace('DD', Math.floor(dd.asDays()).toString()));
      }
    };
  }

  if (newTime < 1000) {
    return '00:00:00';
  }

  return dd
    .format('天HH:mm:ss')
    .replace('undefined', '00')
    .replace('天', `${Math.floor(dd.asDays()).toString()}天`);
}

export function jumpPay(item: any = {}) {
  const query = qsParse<{
    shopId: string;
    orderSource: string;
    oid?: string;
    aid?: string;
    channel?: string;
    channelNo?: string;
  }>();
  const { oid, aid, channel, channelNo } = query;
  window.location.href = `https://act.tinman.cn/team/confirm?${QS.stringify({
    ...item,
    orderSource: qsParse<{ orderSource: string }>().orderSource || Cookies.get('orderSource'),
    shopId: qsParse<{ shopId: string }>().shopId || Cookies.get('shopId'),
    oid,
    aid,
    channel,
    channelNo
  })}`;
}

// 判断元素是否存在与视口中
export function isInViewPort(element: HTMLElement) {
  try {
    const viewWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewHeight = window.innerHeight || document.documentElement.clientHeight;

    const { top, right, bottom, left } = element.getBoundingClientRect();

    return top >= 0 && left >= 0 && right <= viewWidth && bottom <= viewHeight;
  } catch (e) {
    return false;
  }
}

export function jojoAppType() {
  const ua = window.navigator.userAgent;
  if (/JoJoAppFrom\/read/gim.test(ua)) {
    // 叫叫APP
    return 0;
  }
  if (/JoJoSherlock/gim.test(ua)) {
    // 新识字APP
    return 1;
  }
  if (/huiben/gim.test(ua)) {
    // 绘本
    return 2;
  }

  if (/pinyin/gim.test(ua)) {
    // 叫叫拼音app
    return 3;
  }

  if (/sherlock/gim.test(ua)) {
    // 叫叫老识字app
    return 4;
  }

  if (/JoJoCalculate/gim.test(ua)) {
    // 叫叫口算app
    return 5;
  }

  return -1;
}

export function queryClear(query?: string | string[]): string {
  if (Array.isArray(query)) {
    return query[0] || '';
  }

  return query || '';
}

/**
 * 获取url上面的参数
 * @param search
 */
export const getQuery = <T>(search = window.location.search) => {
  const searchParams: any = new URLSearchParams(search);
  const query: Record<string, string> = {};
  for (const [key, value] of searchParams) {
    query[key] = value;
  }
  return query as unknown as T;
};
/**
 * qs objTO url参数
 * @param {*} obj
 * @returns
 */
export function parseQuery(obj: Record<string, any> = {}) {
  const searchKeys = Object.keys(obj);
  let searchStr = '';
  for (let i = 0; i < searchKeys.length; i++) {
    searchStr = `${searchStr + (searchStr ? '&' : '') + searchKeys[i]}=${encodeURIComponent(
      obj[searchKeys[i]]
    )}`;
  }
  return searchStr;
}
/**
 * url新增参数
 * @param {*} url
 * @param {*} keys
 * @param {*} value
 * @returns
 */
export function urlAddObjQuery(url = window.location.href, obj: Record<string, any> = {}) {
  if (!url) {
    return '';
  }
  try {
    const search: Record<string, any> = getQuery(new URL(url).search);
    const urlPath = url.split('?')[0];
    const nsearch = { ...search, ...obj };
    return `${urlPath}?${parseQuery({
      ...nsearch
    })}`;
  } catch (error) {
    return url;
  }
}

export const qsStringify = (query?: any) => {
  const _query = qsParse();
  const _url = [
    window.location.origin,
    window.location.pathname,
    '?',
    QS.stringify({ ..._query, ...(query || {}) })
  ].join('');
  return _url;
};

type AuthorizeType = {
  appId?: string; //
  mode?: number; // 1 登录， 2 不登录
  wechatAuthType?: number; // 1 用户授权 2 静默授权
  type?: string; // 支付场景 pay ｜ 回传场景 marketing
  authBizType?: number; // 1 营销业务 (用于加老师页处静默授权)
  packageName?: string; // (用于加老师页处静默授权)
  reqUrl?: string; // (用于加老师页处静默授权)
};

// 静默授权
export function toAuthrize({ appId, mode, wechatAuthType, type }: AuthorizeType) {
  const searchParams = window.location.search;
  let url = qsStringify({ type: type || '' });
  try {
    if (type !== 'marketing') {
      const redirectParams = storage.get(AUTH_REDIRECT_PARAMS) || '';
      // 跳转用的url参数已经存在于当前url（如：第一次授权失败已经写入url参数，第二次授权时）
      const paramsExist = searchParams.indexOf('vPhone') >= 0;
      if (redirectParams) {
        if (searchParams.length > 0 && !paramsExist) {
          url += redirectParams ? `&${redirectParams}` : '';
        } else if (searchParams.length <= 0 && !paramsExist) {
          url += redirectParams ? `?${redirectParams}` : '';
        }
      }
    }
  } catch (e) {
    reportError(e);
  }
  // 重定向地址添加type类型
  const authWechatAppId = appId ? `authWechatAppId=${appId}&` : '';
  const modeStr = mode ? `mode=${mode}&` : '';
  const wechatAuthTypeStr = wechatAuthType ? `wechatAuthType=${wechatAuthType}&` : '';
  const redirectUrl = [
    AUTH_SIGN_URL,
    '?',
    modeStr,
    authWechatAppId,
    wechatAuthTypeStr,
    'requestUrl=',
    encodeURIComponent(url)
  ].join('');
  // window.location.replace(redirectUrl);
  return redirectUrl;
}

// 获取二级域名
export function getSubDomain() {
  try {
    let subdomain = '';
    const { domain } = document;
    const domainList = domain.split('.');

    const ipAddressReg =
      /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/;

    /* 若当前域名为 IP 地址、localhost，采用一种特殊的处理
      （只用于我们的测试环境，此部分代码不具有通用性）
       */
    if (ipAddressReg.test(domain) || domain === 'localhost') {
      const level3domain = document.domain;
      const pattern = /^.*?\b\.\b/;
      return level3domain.replace(pattern, '');
    }

    const urlItems = [];
    urlItems.unshift(domainList.pop());

    while (urlItems.length < 2) {
      urlItems.unshift(domainList.pop());
      subdomain = urlItems.join('.');
    }

    return `.${subdomain}` || document.domain;
  } catch (e) {
    return document.domain;
  }
}
export function setSubDomainCookies(key: string, value: string, options?: any) {
  return Cookies.set(key, value, {
    domain: getSubDomain(),
    path: '/',
    ...(options || {})
  });
}
export const isX5Browser = () => {
  if (!isBrowser) {
    return false;
  }
  const ua = window.navigator.userAgent.toLocaleLowerCase();
  return isJoJoBrowser() && /mqqbrowser/.test(ua);
};
const baseZoom = POSTER_ZOOM;
export function fontOffset(textAlign: string, fontSize = 14) {
  const fontBaseSize = fontSize / baseZoom;
  const yOffset = Math.round(fontBaseSize / 2);

  let xOffset = 0;
  switch (textAlign) {
    case 'center': {
      xOffset = Math.round(fontBaseSize * 2);
      break;
    }
    case 'right': {
      xOffset = Math.round(fontBaseSize * 4);
      break;
    }
    default:
  }

  return {
    xOffset,
    yOffset
  };
}

export function renderText({
  moduleData,
  conf: cfg,
  unit = '',
  unitStyle,
  maxLen = 6
}: any): StructureType {
  const conf = cfg;
  if (!conf.fontSize) {
    conf.fontSize = 14;
  }

  const ost = fontOffset(conf.layout, conf.fontSize);

  let txt = '';
  let ct = moduleData;

  const dfUnit = unit && unitStyle;

  if (unit && !unitStyle) {
    const num = Number(moduleData);
    txt = num > 9999 ? `${(num / 10000).toFixed(2)}万` : moduleData;
    ct = `${txt}${unit}`;
  } else if (dfUnit) {
    ct = moduleData;
  } else {
    const regex = new RegExp(`(.{${maxLen}}).*`);
    ct = `${moduleData || ''}`.replace(regex, '$1...');
  }

  return {
    type: 'font',
    content: ct,
    unit,
    unitStyle: dfUnit
      ? {
          fontSize: Math.round((unitStyle.fontSize || conf.fontSize) / baseZoom),
          fontWeight: 500,
          color: unitStyle.color ? `#${unitStyle.color}` : '#000'
        }
      : undefined,
    style: {
      x: conf.x + ost.xOffset,
      y: conf.y + ost.yOffset,
      fontSize: Math.round(conf.fontSize / baseZoom),
      color: conf.color ? `#${conf.color}` : '#000',
      textAlign: conf.layout || 'left',
      fontWeight: 500
    }
  };
}

/**
 * 将数字变成偶数
 * @param num 数据
 * @returns 数据
 */
export const becomeAnEvenNumber = (number: number | string) => {
  let num = typeof number === 'number' ? number : parseInt(number, 10);
  const isEvenNumber = num % 2 === 0;

  if (isEvenNumber) {
    return {
      width: num,
      height: num
    };
  }

  num += 1;
  return {
    width: num,
    height: num
  };
};

/**
 * 随机生成固定长度的英文字符串
 * @param length 字符串长度
 * @returns 数据
 */

export function generateRandomString(length: number) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const getAppVersion = () => {
  try {
    if (isJoJoAllBrowser()) {
      const reg = /jojowebviewsymbol\/([\d.]+)/;
      const ua = window.navigator.userAgent.toLowerCase();
      const result = ua.match(reg);

      if (result) {
        return result[1];
      }
    }
  } catch {
    return '';
  }
  return '';
};

export async function getAppAudit(): Promise<{
  audit: boolean;
  guest: boolean;
}> {
  try {
    if (isIOS() && isJoJoReadBrowser()) {
      const bridge: any = new JsBridge();
      const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
      if (canUseBridge) {
        const { status, data } = await bridge.call('getDeviceInfo');
        if (status === 200 && data) {
          return {
            audit: data.iosAudit === '1',
            guest: data.iosAuditVisitor === '1'
          };
        }
      }
    }
  } catch (e) {
    console.error(e);
  }

  return {
    audit: false,
    guest: false
  };
}

function matchMiniAppId(appIds: string[]) {
  if (!isBrowser) {
    return false;
  }
  const inWxMini = isWXMiniAppBrowser();
  const ua = window.navigator.userAgent.toLocaleLowerCase();
  const isDebug = ua.indexOf('webdebugger') > -1;
  const miniAppId = Cookies.get('miniAppId') || (ua?.split?.('miniprogram/')?.[1] ?? '');
  return inWxMini && (appIds.includes(miniAppId) || isDebug);
}

/**
 * 检测浏览器环境是否是叫叫家长端小程序
 * eg：
 */
// wx6937868d2c47cac1 叫叫家长小帮手测试
// wx21ae2ebd76fa1ef6 叫叫家长端
export const isMiniAppAndParent = () =>
  matchMiniAppId(['wx21ae2ebd76fa1ef6', 'wx6937868d2c47cac1']);

export const throttle = (func: (...args: any[]) => void, wait: number) => {
  let lastTime = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastTime >= wait) {
      func(...args);
      lastTime = now;
    }
  };
};

export const intersectionObserver = (
  dom: HTMLElement,
  opts?: {
    root: HTMLElement | null; // 视口（null 表示使用默认的浏览器视口）
    rootMargin: string; // 视口边距
    threshold: number; // 触发回调的可见比例（0.1 表示元素 10% 可见时触发）
  }
) => {
  // 创建 IntersectionObserver 实例
  return new Promise<'nosupport' | 'enter' | 'leave'>((resolve) => {
    if (!('IntersectionObserver' in window)) {
      // 这里可以动态加载 Polyfill
      console.log('当前浏览器不支持 IntersectionObserver');
      resolve('nosupport');
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              resolve('enter');
              // 这里可以执行你的操作，例如加载图片、触发动画等
            } else {
              resolve('leave');
            }
          });
        },
        {
          root: null, // 视口（null 表示使用默认的浏览器视口）
          rootMargin: '0px', // 视口边距
          threshold: 0.1, // 触发回调的可见比例（0.1 表示元素 10% 可见时触发）
          ...(opts || {})
        }
      );
      observer.observe(dom);
    }
  });
};

export function getImageInfo(url: string) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = url;
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
    };
    image.onerror = (error) => {
      reject(error);
    };
  });
}
